import React, { useEffect, useState } from "react";

import { t } from "i18next";
import moment from "moment";

import { DateInput } from "components";

import { useAppSelector } from "store/rootReducer";

import { LoadSpinner } from "features/LoadSpinner";

import { CheckBoxConfidential } from "components/CheckBoxConfidetial";
import { Drawer } from "components/Drawer";
import { DropdownGlobal } from "components/DropdownGlobal";
import TimeInput from "components/TimeInput";

import { AttendantService } from "services/attendant";
import { audienceRecordService } from "services/audienceRecord";
import { hearingFileServiceV2, IHearingFileBody } from "services/hearingFile";
import { hearingFileTypeAttendantRole } from "services/hearingFileTypeAttendantRole";

import { LogOperation } from "utils/entities/logOperation";

import { CreateOitivaConstants } from "./constants";
import { IContainerOitiva } from "./interface";
import { Container, ContainerTwoComponents } from "./styles";

const FORMAT = "YYYY-MM-DD HH:mm";

export const CreateOitiva: React.FC<IContainerOitiva> = ({
  title,
  isEdit,
  setClose,
  isOpen,
  data,
  addToast,
  recordToEdit,
  setRecordEditedReturn,
  date,
  setDate,
  time,
  setTime,
}) => {
  const [dateError, setDateError] = useState<boolean>(false);
  const [timeError, setTimeError] = useState<boolean>(false);
  const [typeError, setTypeError] = useState<boolean>(false);
  const [attendantError, setAttendantError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [hearingFileTypeSelected, setHearingFileTypeSelected] =
    useState<any>("");
  const [attendantSelected, setAttendantSelected] = useState<any>("");
  const [isConfidential, setIsConfidential] = useState(false);
  const [typeOptions, setTypeOptions] = useState<
    Array<{ id: number | string; name: string }>
  >([]);
  const [attendantOptions, setAttendantOptions] = useState<
    Array<{ id: number | string; name: string }>
  >([]);
  const { user } = useAppSelector((state) => state.user);

  const getSelectedOptionId = (value: any) => {
    if (!value) return undefined;
    if (typeof value === "object" && value !== null) {
      return value.id !== undefined && value.id !== null
        ? Number(value.id)
        : undefined;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const selectedHearingFileTypeId = getSelectedOptionId(
    hearingFileTypeSelected,
  );

  const cleanAll = () => {
    setDateError(false);
    setTimeError(false);
    setTypeError(false);
    setAttendantError(false);
    setDate("");
    setTime("");
    setHearingFileTypeSelected("");
    setAttendantSelected("");
    setAttendantOptions([]);
    setIsConfidential(false);
  };

  const handleClose = () => {
    cleanAll();
    setClose();
  };

  useEffect(() => {
    if (!isOpen) {
      cleanAll();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user?.customerId) {
      setTypeOptions([]);
      return;
    }

    (async () => {
      const response = await hearingFileTypeAttendantRole.getAll(
        user.customerId,
        1,
      );

      const hearingFileTypes = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const normalizedHearingFileTypes = hearingFileTypes
        .filter((item: any) => item?.active !== false)
        .map((item: any) => ({
          id: item.id,
          name: item.name,
        }));

      setTypeOptions(normalizedHearingFileTypes);
    })();
  }, [user?.customerId]);

  useEffect(() => {
    if (!isOpen) return;

    const selectedHearingFileType = typeOptions.find(
      (item) => Number(item.id) === Number(recordToEdit?.hearingFileTypeId),
    );

    setHearingFileTypeSelected(selectedHearingFileType || "");
    setAttendantSelected("");
    setIsConfidential(!!recordToEdit?.confidential);
  }, [isOpen, recordToEdit, typeOptions]);

  useEffect(() => {
    const hearingId = data?.hearingId ?? recordToEdit?.hearingId;

    if (!hearingId || !selectedHearingFileTypeId) {
      setAttendantOptions([]);
      setAttendantSelected("");
      return;
    }

    (async () => {
      const hearingFileTypeId = selectedHearingFileTypeId;

      const response = await AttendantService.getAllByHearingFileType({
        hearingId,
        hearingFileTypeId,
      });

      const attendants = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const normalizedAttendants = attendants
        .filter((item: any) => item?.active !== false)
        .map((item: any) => ({
          id: item.attendantId ?? item.id,
          name: item.role ? `${item.name} - ${item.role}` : item.name,
        }));

      setAttendantOptions(normalizedAttendants);

      if (!recordToEdit?.attendantId) {
        return;
      }

      const selectedAttendant = normalizedAttendants.find(
        (item: { id: number | string; name: string }) =>
          Number(item.id) === Number(recordToEdit.attendantId),
      );

      setAttendantSelected(selectedAttendant || "");
    })();
  }, [
    data?.hearingId,
    recordToEdit?.attendantId,
    recordToEdit?.hearingId,
    selectedHearingFileTypeId,
  ]);

  const validateRequiredFields = () => {
    const hasType = !!getSelectedOptionId(hearingFileTypeSelected);
    const hasAttendant = !!getSelectedOptionId(attendantSelected);

    if (date && time && hasType && hasAttendant) {
      return true;
    }

    setDateError(!date);
    setTimeError(!time);
    setTypeError(!hasType);
    setAttendantError(!hasAttendant);
    addToast({
      type: "warning",
      title: t(CreateOitivaConstants.FILL_FIELDS_TITLE),
      description: t(CreateOitivaConstants.FILL_FIELDS_DESCRIPTION),
    });
    return false;
  };

  const validateInputDate = () => {
    const inputDate = moment(`${date} ${time}`, FORMAT);
    const today = moment().startOf("minute");

    if (!inputDate.isBefore(today)) {
      return true;
    }

    setDateError(true);
    setTimeError(true);
    addToast({
      type: "warning",
      title: t(CreateOitivaConstants.INVALID_DATE_TITLE),
      description: t(CreateOitivaConstants.INVALID_DATE_DESCRIPTION),
    });
    return false;
  };

  const buildExpectedDate = () => new Date(`${date}T${time}Z`);

  const handleCreateOitiva = async (expectedDate: Date) => {
    const response = await audienceRecordService.createAudienceRecord({
      expectedDate,
      hearingId: data?.hearingId || 0,
      userId: data?.userId || 0,
      hearingFileTypeId: getSelectedOptionId(hearingFileTypeSelected),
      confidential: isConfidential,
      attendantId: getSelectedOptionId(attendantSelected),
    });
    await LogOperation.postLogOperation("HearingFile", 1, 1);

    if (!response.status) {
      return;
    }

    handleClose();
    addToast({
      type: "success",
      title: t(CreateOitivaConstants.CREATED_TITLE),
      description: t(CreateOitivaConstants.CREATED_DESCRIPTION),
    });
  };

  const handleEditOitiva = async (expectedDate: Date) => {
    if (!recordToEdit) {
      addToast({
        type: "error",
        title: t(CreateOitivaConstants.NOT_FOUND_TITLE),
        description: t(CreateOitivaConstants.NOT_FOUND_DESCRIPTION),
      });
      return;
    }

    const updateBody: IHearingFileBody = {
      ...recordToEdit,
      updateIn: new Date(),
      expectedDate,
      hearingFileTypeId: getSelectedOptionId(hearingFileTypeSelected) ?? null,
      confidential: isConfidential,
      attendantId: getSelectedOptionId(attendantSelected) ?? null,
    };
    const response = await hearingFileServiceV2.update(updateBody);

    if (response.status !== 200) {
      addToast({
        type: "error",
        title: t(CreateOitivaConstants.EDITED_TITLE),
        description: t(CreateOitivaConstants.EDIT_ERROR_DESCRIPTION),
      });
      return;
    }

    setRecordEditedReturn?.(updateBody);
    handleClose();
    addToast({
      type: "success",
      title: t(CreateOitivaConstants.EDITED_TITLE),
      description: t(CreateOitivaConstants.EDITED_DESCRIPTION),
    });
  };

  const handleApplyButton = async () => {
    if (!validateRequiredFields() || !validateInputDate()) {
      return;
    }

    setDateError(false);
    setTimeError(false);
    setLoading(true);

    try {
      const expectedDate = buildExpectedDate();

      if (!isEdit) {
        await handleCreateOitiva(expectedDate);
        return;
      }

      await handleEditOitiva(expectedDate);
    } finally {
      setLoading(false);
    }
  };

  const checkDateAndTime = (date?: string, time?: string) => {
    if (!date || !time) {
      setDateError(!date);
      setTimeError(!time);
      return;
    }

    const input = moment(`${date} ${time}`, FORMAT, true);
    if (!input.isValid()) {
      setDateError(true);
      setTimeError(true);
      return;
    }

    const now = moment();

    if (input.isSame(now, "day")) {
      setDateError(false);
      setTimeError(input.isBefore(now, "minute"));
      return;
    }

    if (input.isBefore(now, "day")) {
      setDateError(true);
      setTimeError(true);
      return;
    }

    setDateError(false);
    setTimeError(false);
  };

  const onChangeDate = (value?: string) => {
    setDate(value);
    checkDateAndTime(value, time);
  };

  const onChangeTime = (value?: string) => {
    setTime(value);
    if (date) {
      checkDateAndTime(date, value);
    }
  };

  const onChangeHearingFileType = (value: any) => {
    setHearingFileTypeSelected(value);
    setTypeError(false);
    setAttendantSelected("");
    setAttendantError(false);
  };

  const onChangeAttendant = (value: any) => {
    setAttendantSelected(value);
    setAttendantError(false);
  };

  return (
    <>
      <Drawer
        id="drawer-create-oitiva"
        open={isOpen}
        onClose={handleClose}
        title={t(isEdit ? "common.editTitle" : "common.createTitle", { title })}
        onApply={handleApplyButton}
        applyLabel=""
        paddingLeft={33}
        paddingRight={33}
      >
        <Container>
          <ContainerTwoComponents>
            <DateInput
              label={t(CreateOitivaConstants.OPEN_DATE)}
              setValue={(e: any) => onChangeDate(e)}
              value={date}
              required={true}
              error={dateError}
            />
            <div style={{ paddingTop: "2px", width: "200px" }}>
              <TimeInput
                label={t(CreateOitivaConstants.TIME)}
                setValue={(e: any) => onChangeTime(e)}
                error={timeError}
                required={false}
                value={time}
              />
            </div>
          </ContainerTwoComponents>

          <DropdownGlobal
            key={`hearing-file-type-${String(hearingFileTypeSelected?.id ?? "")}-${String(hearingFileTypeSelected?.name ?? "")}`}
            withoutMarginBottom
            width="100%"
            error={typeError}
            editDropdown={hearingFileTypeSelected}
            labelName={t(CreateOitivaConstants.TYPES)}
            options={typeOptions}
            required={true}
            setSelected={onChangeHearingFileType}
            wantAnObject={true}
          />

          <DropdownGlobal
            key={`attendant-${String(attendantSelected?.id ?? "")}-${String(attendantSelected?.name ?? "")}`}
            withoutMarginBottom
            width="100%"
            error={attendantError}
            editDropdown={attendantSelected}
            labelName={t(CreateOitivaConstants.ATTENDANTS)}
            options={attendantOptions}
            required={true}
            setSelected={onChangeAttendant}
            wantAnObject={true}
            disabled={!selectedHearingFileTypeId}
          />

          <CheckBoxConfidential
            label={t(CreateOitivaConstants.CONFIDENTIAL)}
            value={isConfidential}
            onChange={setIsConfidential}
          />
        </Container>
      </Drawer>
      <LoadSpinner loading={loading} />
    </>
  );
};

export default CreateOitiva;
